import { ImageUriUpload } from '@spike/model';
import { useTheme, useMediaQuery } from '@material-ui/core';
import clsx from 'clsx';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import isEmpty from 'lodash/isEmpty';
import { ChangeEvent, FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { CropperImage, MobilePhotoSourceSheet } from 'components/UI';
import { MAX_IMAGE_UPLOAD_BYTES, TARGET_IMAGE_MB, resizeFileToMaxBytes } from 'utils/ImageResizeUtils';
import { convertImageFileToJpeg, isPhotosGalleryFormatAllowed, isRasterImageFile } from 'utils/imageFormatToJpeg';
import { getGalleryFileAccept, isIOSDevice } from 'utils/mobileImageGalleryAcept';
import AddPhoto from './AddPhoto';
import Photo from './Photo';
import styles from './PhotosSelector.module.scss';
import PhotosSpecifications from './PhotosSpecifications';

export interface PhotoWrapper {
    id?: number;
    url: string;
    fileToUpload?: File | ImageUriUpload | null;
    destroy?: boolean;
}

export type PhotosSelectorSize = 'small' | 'regular';

interface PhotosSelectorProps {
    title?: string;
    maxPhotos?: number;
    photos: Array<PhotoWrapper>;
    showCoverTag?: boolean;
    multipleSelection?: boolean;
    size?: PhotosSelectorSize;
    className?: string;
    onChange?: (photos: Array<PhotoWrapper>) => void;
    useCropper?: boolean;
    cropWidth?: number;
    cropHeight?: number;
    cropViewScale?: number;
}

const formatErrorMessage = (file: File) =>
    `Image ${file.name || 'file'} has an unsupported file format. Please upload a JPG, PNG or HEIC file.`;

const processUploadedPhotoFile = async (file: File): Promise<{ file: File; error?: string }> => {
    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
        return { file, error: `Image ${file.name || 'file'} size exceeds the 12MB upload limit.` };
    }

    let working = file;

    if (!isPhotosGalleryFormatAllowed(file)) {
        if (!isRasterImageFile(file)) {
            return { file, error: formatErrorMessage(file) };
        }
        try {
            working = await convertImageFileToJpeg(file);
        } catch {
            return {
                file,
                error: `Could not read ${file.name || 'file'}. Try JPG or PNG.`
            };
        }
        if (working.size > MAX_IMAGE_UPLOAD_BYTES) {
            return { file: working, error: `Image ${file.name || 'file'} size exceeds the 12MB upload limit.` };
        }
    }

    const normalizedFile =
        working.size > TARGET_IMAGE_MB * 1024 * 1024
            ? await resizeFileToMaxBytes(working, { targetMb: TARGET_IMAGE_MB })
            : working;

    return { file: normalizedFile };
};

const findFile = (files: Array<File>, file: File) => {
    return files.find(
        f =>
            f.name === file.name && f.size === file.size && f.type === file.type && f.lastModified === file.lastModified
    );
};

const convertToPhotoWrapper = (file: File): PhotoWrapper => {
    return {
        url: URL.createObjectURL(file),
        fileToUpload: file
    };
};

interface PendingCropItem {
    file: File;
    previewUrl: string;
}

const PhotosSelector: FunctionComponent<PhotosSelectorProps> = props => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTouchMobile =
        typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
    const useActionsSheet = isMobile || isTouchMobile;

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const galleryInputRef = useRef<HTMLInputElement | null>(null);
    const cameraInputRef = useRef<HTMLInputElement | null>(null);

    const maxPhotos = props.maxPhotos ?? 10;
    const cropW = props.cropWidth ?? 400;
    const cropH = props.cropHeight ?? 400;
    const cropVs = props.cropViewScale ?? 0.5;

    const [error, setError] = useState<string | Array<string> | null>(null);
    const [showActionsSheet, setShowActionsSheet] = useState(false);
    const [cropQueue, setCropQueue] = useState<Array<PendingCropItem>>([]);

    const validFiles = useMemo(() => {
        return props.photos.filter(photo => !photo.destroy);
    }, [props.photos]);

    const photosRef = useRef(props.photos);
    useEffect(() => {
        photosRef.current = props.photos;
    }, [props.photos]);

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const inputEl = event.target;
        const filesToUpload = inputEl.files;

        if (!filesToUpload || filesToUpload.length === 0) {
            inputEl.value = '';
            return;
        }

        try {
            const isMultipleSelection = props.multipleSelection ?? false;

            if (isMultipleSelection && filesToUpload.length > maxPhotos) {
                setError(`You can only upload up to ${maxPhotos} photos.`);
                return;
            }

            const files = Array.from(filesToUpload);

            const filesWithErrors = await Promise.all(
                files.map(async file => {
                    const { file: processed, error } = await processUploadedPhotoFile(file);
                    if (error !== undefined) {
                        return { url: '', fileToUpload: file, error };
                    }
                    return { ...convertToPhotoWrapper(processed), error: undefined };
                })
            );

            const existingFiles = props.photos
                .filter(photo => photo.fileToUpload)
                .map(photo => photo.fileToUpload) as Array<File>;

            const validFiles: Array<PhotoWrapper> = filesWithErrors
                .filter(file => file.error === undefined)
                .map(item => ({
                    url: item.url,
                    fileToUpload: item.fileToUpload as File
                }));

            const errors = filesWithErrors
                .filter(file => file.error !== undefined)
                .map(file => file.error) as Array<string>;

            if (!isMultipleSelection && errors.length > 0) {
                setError(errors[0]);
                return;
            }

            const duplicateFiltered = validFiles.filter(file => !findFile(existingFiles, file.fileToUpload as File));

            const activeCount = props.photos.filter(photo => !photo.destroy).length;
            const slotsLeft = Math.max(0, maxPhotos - activeCount);

            if (props.useCropper && duplicateFiltered.length > 0) {
                const toCrop = duplicateFiltered.slice(0, slotsLeft);
                if (duplicateFiltered.length > slotsLeft) {
                    errors.unshift(`You can only upload up to ${maxPhotos} photos.`);
                }
                if (toCrop.length > 0) {
                    setCropQueue(prev => [
                        ...prev,
                        ...toCrop.map(vf => ({
                            file: vf.fileToUpload as File,
                            previewUrl: vf.url
                        }))
                    ]);
                }
                if (isMultipleSelection && errors.length > 0) {
                    setError(errors);
                } else if (errors.length > 0) {
                    setError(errors[0]);
                } else {
                    setError(null);
                }
                return;
            }

            let newPhotos = [...props.photos, ...duplicateFiltered];

            if (newPhotos.filter(photo => !photo.destroy).length > maxPhotos) {
                newPhotos = newPhotos.slice(0, maxPhotos);
                errors.unshift(`You can only upload up to ${maxPhotos} photos.`);
            }

            if (isMultipleSelection && errors.length > 0) {
                props.onChange?.(newPhotos);
                setError(errors);
                return;
            }

            setError(null);

            props.onChange?.(newPhotos);
        } finally {
            inputEl.value = '';
        }
    };

    const handleCropComplete = (croppedFile: File) => {
        setCropQueue(prev => {
            if (prev.length === 0) return prev;
            const [current, ...rest] = prev;
            URL.revokeObjectURL(current.previewUrl);
            const newPhoto = convertToPhotoWrapper(croppedFile);
            props.onChange?.([...photosRef.current, newPhoto]);
            return rest;
        });
    };

    const handleCropClose = () => {
        setCropQueue(prev => {
            if (prev.length === 0) return prev;
            const [current, ...rest] = prev;
            URL.revokeObjectURL(current.previewUrl);
            return rest;
        });
    };

    const handleAddPhoto = () => {
        if (isIOSDevice()) {
            openGalleryPicker();
            return;
        }
        if (useActionsSheet) {
            setShowActionsSheet(true);
        } else {
            fileInputRef.current?.click();
        }
    };

    const openGalleryPicker = () => {
        galleryInputRef.current?.click();
        setShowActionsSheet(false);
    };

    const openCameraPicker = () => {
        cameraInputRef.current?.click();
        setShowActionsSheet(false);
    };

    const handleRemovePhoto = (url: string) => {
        props.onChange?.(
            props.photos
                .map(photo => (photo.url === url ? (!photo.fileToUpload ? { ...photo, destroy: true } : null) : photo))
                .filter(photo => photo !== null)
        );
    };

    return (
        <div className={clsx(styles.container, props.className, props.size && styles[props.size])}>
            {!isEmpty(props.title) && <Label className={styles.title}>{props.title}</Label>}

            <div className={clsx(styles.photosContainer)}>
                {props.photos
                    .filter(photo => !(photo.destroy ?? false))
                    .map((photo, index) => (
                        <Photo
                            key={index}
                            isCover={props.showCoverTag && index === 0}
                            src={photo.url}
                            onRemove={() => handleRemovePhoto(photo.url)}
                        />
                    ))}

                {validFiles.length < maxPhotos && <AddPhoto onClick={handleAddPhoto} />}
            </div>

            <PhotosSpecifications
                maxPhotos={maxPhotos}
                className={styles.specifications}
            />

            {error !== null &&
                (typeof error === 'string' ? (
                    <span className={styles.fileErrorMessage}>{error}</span>
                ) : (
                    error.map((error, index) => (
                        <span
                            key={index}
                            className={styles.fileErrorMessage}
                        >
                            {error}
                        </span>
                    ))
                ))}

            {useActionsSheet ? (
                <>
                    <input
                        type="file"
                        accept={getGalleryFileAccept()}
                        multiple={props.multipleSelection}
                        style={{
                            position: 'absolute',
                            width: 0,
                            height: 0,
                            opacity: 0,
                            overflow: 'hidden'
                        }}
                        aria-hidden
                        tabIndex={-1}
                        ref={galleryInputRef}
                        onChange={handleFileChange}
                    />
                    <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{
                            position: 'absolute',
                            width: 0,
                            height: 0,
                            opacity: 0,
                            overflow: 'hidden'
                        }}
                        aria-hidden
                        tabIndex={-1}
                        ref={cameraInputRef}
                        onChange={handleFileChange}
                    />
                </>
            ) : (
                <input
                    type="file"
                    accept={getGalleryFileAccept()}
                    multiple={props.multipleSelection}
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={handleFileChange}
                />
            )}

            {useActionsSheet && (
                <MobilePhotoSourceSheet
                    open={showActionsSheet}
                    onClose={() => setShowActionsSheet(false)}
                    onPhotoGallery={openGalleryPicker}
                    onCamera={openCameraPicker}
                />
            )}

            {props.useCropper && cropQueue.length > 0 && (
                <CropperImage
                    image={cropQueue[0].previewUrl}
                    fileName={cropQueue[0].file.name || 'photo.jpg'}
                    cropWidth={cropW}
                    cropHeight={cropH}
                    viewScale={cropVs}
                    onCrop={handleCropComplete}
                    onClose={handleCropClose}
                />
            )}
        </div>
    );
};

export default PhotosSelector;
